import { useQuery } from '@apollo/client';
import { Box, Grid, Paper } from '@mui/material';
import { eachMonthOfInterval } from 'date-fns';
import * as Schema from 'generated/graphql/schema';
import { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSound from 'use-sound';

import AutosuggestCardHeader from '@/components/chip-filter/autosuggest-card-header';
import ResponsiveCardContent from '@/components/containers/responsive-card-content';
import ResponsiveGridContainer from '@/components/containers/responsive-grid-container';
import TimePicker from '@/components/pickers/time-picker';
import ProgressBar from '@/components/progress-bar';
import CompanyContext from '@/contexts/company';
import { useTimePicker } from '@/contexts/timepicker';
import { andonListCalls } from '@/graphql/queries';
import notification from '@/sounds/notification.mp3';
import * as Types from '@/types';
import AndonTable from '@/views/andon/table';

// FIXME: Generalize this type
interface Option {
  name: string;
  value: string;
}

export interface Properties {}

const getFirstOfMonths = (from: Date, to: Date) => {
  const firstOfMonthsLocal = eachMonthOfInterval({ start: from, end: to });

  return firstOfMonthsLocal.map(
    (firstOfMonth) => new Date(firstOfMonth.getTime() - firstOfMonth.getTimezoneOffset() * (1).minutes),
  );
};

const dependentActions = {
  resolve: 'pending' as 'pending',
  pending: 'take' as 'take',
  take: 'make' as 'make',
};

const CallsOverview: FunctionComponent<Properties> = () => {
  const { from, to } = useTimePicker();

  const [t] = useTranslation(['shared', 'andon']);
  const allFirstOfMonths = useMemo(() => getFirstOfMonths(from, to || new Date()), [from.getTime(), to?.getTime()]);

  const firstOfMonthsExceptCurrent = allFirstOfMonths.filter((firstOfMonth) => {
    const now = new Date();
    return now.getUTCFullYear() !== firstOfMonth.getUTCFullYear() || now.getUTCMonth() !== firstOfMonth.getUTCMonth();
  });

  const { data, networkStatus, fetchMore } = useQuery<Schema.AndonListCallsQuery, Schema.AndonListCallsQueryVariables>(
    andonListCalls,
    {
      fetchPolicy: 'cache-and-network',
      ssr: false,
      pollInterval: (20).seconds,
      notifyOnNetworkStatusChange: true,
    },
  );

  useEffect(() => {
    firstOfMonthsExceptCurrent.forEach(async (firstOfMonth) =>
      fetchMore({
        variables: { time: firstOfMonth },
      }),
    );
  }, [from, to]);

  const [primaryAndon] = data?.andon || [];

  const { calls = [], companyId } = primaryAndon || {};

  const newestCallTime = useMemo(
    () =>
      calls.reduce((latestTimestamp, call) => {
        return Math.max(latestTimestamp, new Date(call.action.make.time).getTime());
      }, -Infinity),
    [calls],
  );

  const [play] = useSound(notification);

  useEffect(() => {
    if (!Number.isFinite(newestCallTime)) {
      return;
    }

    play();
  }, [newestCallTime]);

  const suggestions = useMemo(() => {
    return [
      {
        name: t(['shared:location'], { defaultValue: 'Location' }),
        key: 'lineId',
        type: 'OPTIONS',
        justOne: true,
        options: calls
          .map((c) => c.line)
          .filter((line): line is NonNullable<typeof line> => !!line)
          .filter((line, i, lines) => lines.findIndex((l) => line.id === l.id) === i)
          .map((line) => ({ name: line.name, value: line.id })),
      },
      {
        name: t(['shared:awaitingAction'], { defaultValue: 'Awaiting action' }),
        key: 'awaitingAction',
        type: 'OPTIONS',
        justOne: true,
        options: [
          { name: t(['andon:take'], { defaultValue: 'Take' }), value: 'take' },
          { name: t(['andon:pending'], { defaultValue: 'Pending' }), value: 'pending' },
          { name: t(['andon:resolve'], { defaultValue: 'Resolve' }), value: 'resolve' },
        ],
      },
      {
        name: t(['shared:caller'], { defaultValue: 'Caller' }),
        key: 'caller',
        type: 'OPTIONS',
        justOne: true,
        options: calls
          .map((c) => c.action.make.author)
          .filter(
            (author, i, authors) =>
              authors.findIndex((a) => author.trim().toLowerCase() === a.trim().toLowerCase()) === i,
          )
          .map((author) => ({ name: author, value: author.trim().toLowerCase() })),
      },
      {
        name: t(['shared:taker'], { defaultValue: 'Taker' }),
        key: 'taker',
        type: 'OPTIONS',
        justOne: true,
        options: calls
          .map((c) => c.action.take?.author)
          .filter((author): author is NonNullable<typeof author> => !!author)
          .filter(
            (author, i, authors) =>
              authors.findIndex((a) => author.trim().toLowerCase() === a.trim().toLowerCase()) === i,
          )
          .map((author) => ({ name: author, value: author.trim().toLowerCase() })),
      },
      {
        name: t(['shared:resolver'], { defaultValue: 'Resolver' }),
        key: 'resolver',
        type: 'OPTIONS',
        justOne: true,
        options: calls
          .map((c) => c.action.resolve?.author)
          .filter((author): author is NonNullable<typeof author> => !!author)
          .filter(
            (author, i, authors) =>
              authors.findIndex((a) => author.trim().toLowerCase() === a.trim().toLowerCase()) === i,
          )
          .map((author) => ({ name: author, value: author.trim().toLowerCase() })),
      },
      {
        name: t(['andon:requestedRole'], { defaultValue: 'Requested Role' }),
        key: 'requestedRole',
        type: 'OPTIONS',
        justOne: true,
        options: calls
          .map((c) => c.requestedSupport)
          .filter((role): role is NonNullable<typeof role> => !!role)
          .filter(
            (role, i, roles) => roles.findIndex((r) => role.trim().toLowerCase() === r.trim().toLowerCase()) === i,
          )
          .map((role) => ({ name: role, value: role.trim().toLowerCase() })),
      },
    ];
  }, [t, calls]);

  const timeFilter = useCallback(
    (call: Schema.AndonListCallsQuery['andon'][0]['calls'][0]) => {
      const actionTime = new Date(
        (call.action.resolve || call.action.pending || call.action.take || call.action.make).time,
      ).getTime();
      return from.getTime() <= actionTime && (!to || actionTime <= to.getTime());
    },
    [from.getTime(), to?.getTime()],
  );

  const [filters, setFilters] = useState<Array<{ key: string; value: string }>>([]);

  const onFilters = useCallback((filters: Array<{ key: string; value: string }>) => {
    const filteredFilters = filters.filter((filter, _, allFilters) => {
      if (!['machine', 'unit'].some((key) => filter.key === key)) {
        return true;
      }

      return allFilters.some((f) => f.key === 'lineId');
    });

    setFilters(filteredFilters);
  }, []);

  // NOTE: This is Lundbeck-specific - they are the only one that uses tags for
  // work orders - we may want to redesign that concept at a later point - leave
  // this as is for now, just know that in 99% of the cases the assignment
  // terminates immediately.
  const extendedSuggestions = useMemo(() => {
    if (!filters.some((filter) => filter.key === 'lineId')) {
      return suggestions;
    }

    if (!calls.some((call) => call.tags?.length)) {
      return suggestions;
    }

    const extraSuggestions = [];

    const [machines, units] = calls
      .map((c) => c.tags)
      .flat()
      .filter((tag): tag is NonNullable<typeof tag> => !!tag)
      .filter((tag, i, tags) => tags.findIndex((t) => t.id === tag.id) === i)
      .reduce(
        ([machines, units], tag) => {
          const trimmedValue = tag.value.trim();
          const [machine, unit = ''] = trimmedValue.split(': ').map((pt) => pt.trim());

          machines[machine] = machines[machine] || { name: machine, value: machine };

          // NOTE: We use the tag.value to avoid looking up units scoped under other lines
          const lowerMachine = machine.toLowerCase();
          units[lowerMachine] = units[lowerMachine] || {};
          units[lowerMachine][trimmedValue] = units[lowerMachine][trimmedValue] || { name: unit, value: trimmedValue };

          return [machines, units];
        },
        [{}, {}] as [Record<string, Option>, Record<string, Record<string, Option>>],
      );

    extraSuggestions.push({
      name: t(['shared:machine'], { defaultValue: 'Machine' }),
      key: 'machine',
      type: 'OPTIONS',
      justOne: true,
      options: Object.values(machines),
    });

    const filteredMachine = filters.find((filter) => filter.key === 'machine');
    if (!filteredMachine) {
      return [...suggestions, ...extraSuggestions];
    }

    const trimmedMachine = filteredMachine.value.trim();

    extraSuggestions.push({
      name: t(['shared:unit'], { defaultValue: 'Unit' }),
      key: 'unit',
      type: 'OPTIONS',
      justOne: true,
      options: Object.values(units[trimmedMachine] || {}),
    });

    return [...suggestions, ...extraSuggestions];
  }, [filters, calls]);

  const filterPredicates = useMemo(() => {
    return [
      ...filters.map(({ key, value }) => (call: Schema.AndonListCallsQuery['andon'][0]['calls'][0]) => {
        switch (key) {
          case 'lineId':
            return value === call.line?.id;
          case 'awaitingAction':
            const dependentAction = Types.has(dependentActions, value) ? dependentActions[value] : null;
            if (!dependentAction) {
              return false;
            }

            return !!call.action[dependentAction] && Types.has(call.action, value) && !call.action[value];
          case 'caller':
            return value.trim().toLowerCase() === call.action.make.author.trim().toLowerCase();
          case 'taker':
            if (!call.action.take) {
              return false;
            }

            return value.trim().toLowerCase() === call.action.take.author.trim().toLowerCase();
          case 'resolver':
            if (!call.action.resolve) {
              return false;
            }

            return value.trim().toLowerCase() === call.action.resolve.author.trim().toLowerCase();
          case 'machine':
          case 'unit':
            return (call.tags || []).some((t) => t?.value.toLowerCase().includes(value));

          case 'requestedRole':
            return value.trim().toLowerCase() === call.requestedSupport?.trim().toLowerCase();
          default:
            return true;
        }
      }),
      timeFilter,
    ];
  }, [filters, timeFilter]);

  return (
    <CompanyContext value={{ companyId }}>
      <ProgressBar reset={networkStatus === 6} />
      <ResponsiveGridContainer>
        <Grid item xs={12}>
          <Paper>
            <ResponsiveCardContent>
              <Grid container spacing={3}>
                <Grid item md={7} xs={12}>
                  <Box mt={1}>
                    <TimePicker />
                  </Box>
                </Grid>
                <Grid item md={5} xs={12}>
                  <AutosuggestCardHeader
                    suggestions={extendedSuggestions}
                    applyFilter={onFilters}
                    t={t}
                    placeholderText={t(['batches:callFilters'], { defaultValue: 'Filter Calls...' })}
                  />
                </Grid>
              </Grid>
              <AndonTable calls={calls} filters={filterPredicates} />
            </ResponsiveCardContent>
          </Paper>
        </Grid>
      </ResponsiveGridContainer>
    </CompanyContext>
  );
};

export default CallsOverview;
